import { getAPI } from "@/lib/api";
import {
  getAll,
  getDeals,
  getDealstageAvgTimesByCompanyId,
  getPipelineRiskDealsWithEmails,
} from "@/lib/supabase";
import { useAppStore } from "@/stores";
import { NestedCompany, NestedCurrency, NestedTeam } from "@revelate/types";
import { isWithinInterval } from "date-fns";
import { useCallback, useMemo } from "react";
import useSWR from "swr";
import {
  evaluateRiskPipelineCoverage,
  evaluateRiskPipelineRisk,
  getDealTopRiskInCateogory,
  getManagementReviewUrgencyRisks,
  prepareDealsForRiskAssessment,
} from "../lib/risk";
import { getStateOfSalesV2ForecastPosition } from "./forecastPosition";
import {
  buildStateOfSalesV2PipelineHealthStages,
  buildStateOfSalesV2PipelineRiskAnalysis,
} from "./pipelineAnalysis";
import { buildPipelineRiskMovement } from "./pipelineRiskMovement";
import {
  filterDealsByGlobalSelection,
  StateOfSalesV2FilterState,
  useStateOfSalesV2Forecast,
} from "./useForecastData";

type ForecastModel = ReturnType<typeof useStateOfSalesV2Forecast>;

function hasExpectedCloseInRange(
  deal: ReturnType<typeof prepareDealsForRiskAssessment>[number],
  range: { from: Date; to: Date },
) {
  if (!deal.adjustedCloseDate) return false;
  const expectedCloseDate = new Date(deal.adjustedCloseDate);
  if (Number.isNaN(expectedCloseDate.getTime())) return false;
  return isWithinInterval(expectedCloseDate, {
    start: range.from,
    end: range.to,
  });
}

function cloneDealsForEvaluation<T>(deals: T[]) {
  return deals.map(deal => structuredClone(deal));
}

export function useStateOfSalesV2PipelineData(
  model: ForecastModel,
  options: { enabled?: boolean } = {},
) {
  const { currentUser, companyCurrency } = useAppStore((state) => state);
  const companyId = currentUser?.company_id?.toString() ?? null;
  const enabled = options.enabled ?? true;
  const swrOptions = {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  };

  const allOpenDealsQuery = useSWR(
    enabled && companyId
      ? ["query:stateOfSalesAllOpenDealsBase", companyId]
      : null,
    ([, id]) => getDeals(id, {
      onlyOpen: true,
      nonZeroValue: true,
    }),
    swrOptions,
  );
  const pipelineRiskDealsQuery = useSWR(
    enabled && companyId && allOpenDealsQuery.data
      ? [
          "query:pipelineRiskEmails",
          companyId,
          allOpenDealsQuery.data.length,
          allOpenDealsQuery.data[0]?.id ?? null,
          allOpenDealsQuery.data.at(-1)?.id ?? null,
        ]
      : null,
    () => getPipelineRiskDealsWithEmails(allOpenDealsQuery.data ?? []),
    swrOptions,
  );
  const currenciesQuery = useSWR(
    enabled && companyId ? ["query:currencies", companyId] : null,
    ([, id]) => getAll("currencies", id),
    swrOptions,
  );
  const teamsQuery = useSWR(
    enabled && companyId ? ["query:teams", companyId] : null,
    ([, id]) => getAll("teams", id),
    swrOptions,
  );
  const pipelinesQuery = useSWR(
    enabled && companyId ? ["query:pipelines", companyId] : null,
    ([, id]) => getAPI(`providers/hubspot/pipelines/${id}`),
    swrOptions,
  );
  const stageTimesQuery = useSWR(
    enabled && companyId ? ["query:dealstageAvgTimes", companyId] : null,
    ([, id]) => id ? getDealstageAvgTimesByCompanyId(Number(id)) : [],
    swrOptions,
  );

  const isLoading = Boolean(
    enabled && (
    !currentUser?.company
    || allOpenDealsQuery.isLoading
    || pipelineRiskDealsQuery.isLoading
    || currenciesQuery.isLoading
    || teamsQuery.isLoading
    || pipelinesQuery.isLoading
    || stageTimesQuery.isLoading
    || model.isLoading)
  );
  const error = allOpenDealsQuery.error
    ?? pipelineRiskDealsQuery.error
    ?? currenciesQuery.error
    ?? teamsQuery.error
    ?? pipelinesQuery.error
    ?? stageTimesQuery.error
    ?? model.error;

  const preparedOpenDeals = useMemo(() => {
    if (!currentUser?.company || !currenciesQuery.data) return [];
    return prepareDealsForRiskAssessment(
      pipelineRiskDealsQuery.data ?? [],
      pipelinesQuery.data,
      currenciesQuery.data as NestedCurrency[],
      currentUser.company as unknown as NestedCompany,
      stageTimesQuery.data,
    );
  }, [
    pipelineRiskDealsQuery.data,
    currenciesQuery.data,
    currentUser?.company,
    pipelinesQuery.data,
    stageTimesQuery.data,
  ]);
  const teams = useMemo(
    () => (teamsQuery.data ?? []) as NestedTeam[],
    [teamsQuery.data],
  );
  const filteredOpenDeals = useMemo(
    () => filterDealsByGlobalSelection(preparedOpenDeals, teams, model.filters),
    [model.filters, preparedOpenDeals, teams],
  );
  const selectedPeriodRange = model.forecast?.stateOfSalesV2SelectedPeriodRange ?? null;
  const selectedPeriodOpenDeals = useMemo(() => {
    if (!selectedPeriodRange || model.period === "prev_quarter") return [];
    return filteredOpenDeals.filter(
      deal => hasExpectedCloseInRange(deal, selectedPeriodRange),
    );
  }, [filteredOpenDeals, model.period, selectedPeriodRange]);
  const forecastPosition = model.forecast
    ? getStateOfSalesV2ForecastPosition(model.forecast, model.period, model.accumulated, model.chartData)
    : null;
  const urgencyDeals = useMemo(
    () => getManagementReviewUrgencyRisks(
      (model.forecast?.stateOfSalesV2OpenDeals ?? [])
        .filter(deal => !deal.is_closed && !deal.is_won && !deal.is_lost),
    ),
    [model.forecast?.stateOfSalesV2OpenDeals],
  );

  const riskEvaluation = useMemo(() => {
    if (!currentUser?.company || isLoading || error) return null;
    return evaluateRiskPipelineRisk(
      cloneDealsForEvaluation(selectedPeriodOpenDeals),
      currentUser.company_id,
      pipelinesQuery.data?.results ?? [],
      "by_pipeline",
      "all",
      "all",
      "largest",
      stageTimesQuery.data,
      companyCurrency,
    );
  }, [
    companyCurrency,
    currentUser?.company,
    currentUser?.company_id,
    error,
    isLoading,
    pipelinesQuery.data?.results,
    selectedPeriodOpenDeals,
    stageTimesQuery.data,
  ]);

  const risk = useMemo(() => {
    if (!riskEvaluation || !forecastPosition || !model.forecast) return null;
    const evaluatedDeals = riskEvaluation.deals.map(deal => ({
      ...deal,
      topRisk: getDealTopRiskInCateogory(deal, "forecast"),
    }));
    return buildStateOfSalesV2PipelineRiskAnalysis(
      evaluatedDeals,
      model.getForecastDealsForMonths(new Set(model.chartData.filter(row => !row.is_context).map(row => row.month))),
      forecastPosition.forecast,
    );
  }, [forecastPosition, model, riskEvaluation]);

  const movement = useMemo(() => {
    if (
      !companyId
      || !currentUser?.company
      || !model.forecast
      || !selectedPeriodRange
      || model.period === "prev_quarter"
      || isLoading
      || error
    ) {
      return null;
    }

    return buildPipelineRiskMovement({
      companyId: currentUser.company_id,
      currentDeals: selectedPeriodOpenDeals,
      dealstageAvgTimes: stageTimesQuery.data,
      forecastDeals: model.forecast.stateOfSalesV2OpenDeals,
      pipelines: pipelinesQuery.data?.results ?? [],
      selectedPeriodRange,
    });
  }, [
    companyId,
    currentUser?.company,
    currentUser?.company_id,
    error,
    isLoading,
    model.forecast,
    model.period,
    pipelinesQuery.data?.results,
    selectedPeriodOpenDeals,
    selectedPeriodRange,
    stageTimesQuery.data,
  ]);

  const healthEvaluation = useMemo(() => {
    if (
      !currentUser?.company
      || isLoading
      || error
      || !forecastPosition
      || !selectedPeriodRange
      || model.period === "prev_quarter"
      || forecastPosition.target <= 0
    ) {
      return null;
    }

    return evaluateRiskPipelineCoverage(
      cloneDealsForEvaluation(selectedPeriodOpenDeals),
      currentUser.company_id,
      [],
      pipelinesQuery.data?.results ?? [],
      forecastPosition.target,
      "all",
      "all",
      "source",
      "source",
      selectedPeriodRange,
    );
  }, [
    currentUser?.company,
    currentUser?.company_id,
    error,
    forecastPosition,
    isLoading,
    model.period,
    pipelinesQuery.data?.results,
    selectedPeriodOpenDeals,
    selectedPeriodRange,
  ]);

  const health = useMemo(() => {
    if (!healthEvaluation || !forecastPosition || !riskEvaluation) return null;
    const evaluatedRiskDealsById = new Map(
      riskEvaluation.deals.map(deal => [deal.id, deal]),
    );
    const stageRows = buildStateOfSalesV2PipelineHealthStages(
      healthEvaluation.chartData,
    );
    const weakestStage = stageRows
      .toSorted((a, b) => a.coveragePercentage - b.coveragePercentage)
      .at(0) ?? null;
    return {
      concentrationDeals: riskEvaluation.concentratedDeals.map(
        deal => evaluatedRiskDealsById.get(deal.id) ?? deal,
      ),
      concentrationDealCount: riskEvaluation.pipelineConcentrationDealQty,
      concentrationLabel: riskEvaluation.pipelineConcentrationShortSummary,
      concentrationPercentage: riskEvaluation.pipelineConcentrationPct,
      coveragePercentage: Number.isFinite(healthEvaluation.coverage)
        ? healthEvaluation.coverage
        : 0,
      openDealCount: selectedPeriodOpenDeals.length,
      rawOpenPipelineValue: selectedPeriodOpenDeals.reduce(
        (sum, deal) => sum + Number(deal.value ?? 0),
        0,
      ),
      stageRows,
      target: forecastPosition.target,
      timeAdjustedPipelineValue: healthEvaluation.pipelineCreated,
      weakestStage,
    };
  }, [forecastPosition, healthEvaluation, riskEvaluation, selectedPeriodOpenDeals]);

  const resolveForFilters = useCallback((filters: StateOfSalesV2FilterState) => {
    if (!currentUser?.company || isLoading || error) return null;
    const scopedForecast = model.evaluateForecastForFilters(filters);
    if (!scopedForecast) return null;
    const scopedRange = scopedForecast.stateOfSalesV2SelectedPeriodRange ?? null;
    const scopedFilteredOpenDeals = filterDealsByGlobalSelection(
      preparedOpenDeals,
      teams,
      filters,
    );
    const scopedSelectedDeals = scopedRange && filters.period !== "prev_quarter"
      ? scopedFilteredOpenDeals.filter(deal => hasExpectedCloseInRange(deal, scopedRange))
      : [];
    const scopedPosition = getStateOfSalesV2ForecastPosition(
      scopedForecast,
      filters.period,
      filters.accumulated,
      model.buildChartDataForFilters(scopedForecast, filters),
    );
    const scopedUrgencyDeals = getManagementReviewUrgencyRisks(
      (scopedForecast.stateOfSalesV2OpenDeals ?? [])
        .filter(deal => !deal.is_closed && !deal.is_won && !deal.is_lost),
    );
    const scopedRiskEvaluation = evaluateRiskPipelineRisk(
      cloneDealsForEvaluation(scopedSelectedDeals),
      currentUser.company_id,
      pipelinesQuery.data?.results ?? [],
      "by_pipeline",
      "all",
      "all",
      "largest",
      stageTimesQuery.data,
      companyCurrency,
    );
    const evaluatedDeals = scopedRiskEvaluation.deals.map(deal => ({
      ...deal,
      topRisk: getDealTopRiskInCateogory(deal, "pipelineRisk"),
    }));
    const scopedRisk = buildStateOfSalesV2PipelineRiskAnalysis(
      evaluatedDeals,
      model.getForecastDealsForMonths(new Set(model.buildChartDataForFilters(scopedForecast, filters)
        .filter(row => !row.is_context).map(row => row.month)), filters),
      scopedPosition.forecast,
    );
    const scopedMovement = !companyId || !scopedRange || filters.period === "prev_quarter"
      ? null
      : buildPipelineRiskMovement({
          companyId: currentUser.company_id,
          currentDeals: scopedSelectedDeals,
          dealstageAvgTimes: stageTimesQuery.data,
          forecastDeals: scopedForecast.stateOfSalesV2OpenDeals,
          pipelines: pipelinesQuery.data?.results ?? [],
          selectedPeriodRange: scopedRange,
        });
    return {
      forecast: scopedForecast,
      forecastPosition: scopedPosition,
      movement: scopedMovement,
      risk: scopedRisk,
      selectedPeriodRange: scopedRange,
      urgencyDeals: scopedUrgencyDeals,
    };
  }, [
    companyCurrency,
    companyId,
    currentUser?.company,
    currentUser?.company_id,
    error,
    isLoading,
    model,
    pipelinesQuery.data?.results,
    preparedOpenDeals,
    stageTimesQuery.data,
    teams,
  ]);

  const retry = () => Promise.all([
    allOpenDealsQuery.mutate(),
    currenciesQuery.mutate(),
    teamsQuery.mutate(),
    pipelinesQuery.mutate(),
    stageTimesQuery.mutate(),
    model.retry(),
  ]);

  return {
    error,
    forecastPosition,
    health,
    isLoading,
    movement,
    risk,
    riskEvaluation,
    retry,
    resolveForFilters,
    selectedPeriodOpenDeals,
    selectedPeriodRange,
    urgencyDeals,
  };
}
